
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const LAST_UPDATED = "October 31, 2026";
const SUPPORT_EMAIL = "support@taptotable.co";

const sections = [
  {
    title: "1. About This Privacy Policy",
    paragraphs: [
      "This Privacy Policy explains how Melted Port Studios LLC collects, uses, stores, and shares information in connection with TapToTable.",
      "This policy applies when you visit the TapToTable website, request that we build a TapToTable Page, review or activate a page, use a TapToTable Dashboard, contact support, or interact with a restaurant page powered by TapToTable.",
    ],
  },
  {
    title: "2. Information You Provide",
    paragraphs: [
      "We may collect information you submit through TapToTable forms, account screens, emails, support requests, and other communications.",
      "This information may include your name, restaurant name, business email address, phone number, Instagram handle, website, address, menu link, ordering link, restaurant description, business hours, Promotions, Events, Featured Items, photographs, logos, and other restaurant information.",
      "Featured Item information may include the item name, description, photograph, menu link, and related promotional details.",
      "When an account is created, we may collect account information such as email address, name, role, login credentials, account status, and dashboard activity.",
      "Please do not submit information you are not authorized to provide or publish.",
    ],
  },
  {
    title: "3. TapToTable Page Requests and Multi-Location Information",
    paragraphs: [
      "When you request that TapToTable build a page, we collect the information included in your request to evaluate the restaurant, prepare the page, contact you, and provide follow-up support.",
      "The signup form may collect your requested campaign type, optional campaign details, restaurant or social-media links, email address, and the number of additional restaurant locations you would like to include.",
      "If you request additional restaurant locations, we may collect or request information about those locations and their ownership to determine eligibility for multi-location pricing.",
      "Each restaurant location may have a separate TapToTable Page, account, login, dashboard, subscription, and related operational records.",
      "Requesting and reviewing a TapToTable Page does not require payment. Information submitted with a page request may be retained even if you choose not to activate a paid subscription.",
    ],
  },
  {
    title: "4. Publicly Available Restaurant Information",
    paragraphs: [
      "To build, review, or improve a TapToTable Page, we may review and use publicly available restaurant information from restaurant websites, online menus, Google Business Profiles, social-media profiles, public business listings, and other publicly accessible sources.",
      "This information may include restaurant names, descriptions, addresses, phone numbers, hours, cuisine categories, ratings, review counts, website links, menu links, ordering links, logos, and publicly displayed photographs.",
      "We use this information to prepare restaurant pages, verify details, improve accuracy, and provide the TapToTable service. Restaurant owners may contact us to request corrections or removal of inaccurate information.",
    ],
  },
  {
    title: "5. Payment and Subscription Information",
    paragraphs: [
      "When you activate a paid TapToTable subscription, payment information is processed by Stripe or another authorized payment provider.",
      "TapToTable may receive limited billing information such as your name, email address, billing status, subscription status, transaction identifiers, payment dates, and limited payment-method details.",
      "For multi-location customers, subscription and billing records may be maintained separately for each restaurant location.",
      "TapToTable does not directly store complete payment-card numbers or card security codes.",
      "Payment providers process information according to their applicable terms and privacy policies.",
    ],
  },
  {
    title: "6. Information Collected Automatically",
    paragraphs: [
      "When someone visits the TapToTable website, a review link, a customer-facing restaurant page, or a TapToTable Dashboard, certain technical and usage information may be collected automatically.",
      "This information may include IP address, browser type, device type, operating system, referring page, pages viewed, timestamps, session information, approximate location derived from an IP address, and interactions with supported buttons or links.",
      "We may record page visits, offer claims, event actions, and other supported customer interactions. The specific information collected and tracked depends on the functionality available for each campaign type.",
      "Technical information may also be processed to maintain website functionality, detect errors, prevent abuse, and protect account security.",
    ],
  },
  {
    title: "7. Customer Activity and Supported Analytics",
    paragraphs: [
      "TapToTable may record when a visitor uses the offer-claim feature, interacts with an Event, visits a restaurant page, or uses a supported customer action.",
      "An offer claim is an online interaction or expression of interest. It does not confirm that a visitor entered the restaurant, completed a purchase, or redeemed an offer.",
      "Featured Item campaigns may generate page-visit activity. Dedicated Featured Item button-click tracking is not currently included, so not every customer action is necessarily reflected in dashboard analytics.",
      "To reduce duplicate activity, TapToTable may use browser-generated visitor identifiers and timestamps when recording certain visits, claims, and event actions.",
      "Analytics may be affected by browser settings, blocked scripts, automated traffic, duplicate activity, network conditions, and technical limitations. These metrics should be considered informational estimates rather than verified business results.",
    ],
  },
  {
    title: "8. Cookies, Sessions, Local Storage, and Attribution",
    paragraphs: [
      "TapToTable may use cookies, browser storage, session tokens, and similar technologies to maintain account access, remember preferences, support website functionality, prevent duplicate activity, and understand service usage.",
      "When someone visits TapToTable through an advertisement or tracked marketing link, we may collect campaign attribution information, including UTM parameters, advertising campaign identifiers, ad set identifiers, ad identifiers, and referral information.",
      "Where present in the URL, we may collect Meta-related click identifiers such as fbclid. This information may be stored in the browser and submitted with a restaurant page request to help us understand which marketing activities generated inquiries.",
      "TapToTable may maintain first-touch and last-touch attribution records so that the original source of a visitor and the most recent tracked source can be associated with a signup.",
      "Your browser may allow you to block or delete cookies and local-storage data. Disabling these technologies may prevent portions of TapToTable, including account authentication and some measurement functions, from working correctly.",
    ],
  },
  {
    title: "9. How We Use Information",
    bullets: [
      "Build and deliver TapToTable Pages for restaurant review",
      "Set up, host, manage, and update TapToTable Pages",
      "Support Promotions, Events, and Featured Item campaigns",
      "Create and administer secure TapToTable Dashboard accounts",
      "Process activations, subscriptions, billing status, and cancellations",
      "Review additional-location requests and eligibility",
      "Communicate with restaurant owners and account users",
      "Provide support and respond to change requests",
      "Measure supported page visits, claims, event actions, and customer activity",
      "Understand signup sources and advertising campaign performance",
      "Improve service reliability, usability, and security",
      "Prevent fraud, automated submissions, abuse, and unauthorized access",
      "Comply with legal obligations and enforce applicable agreements",
    ],
  },
  {
    title: "10. How We Share Information",
    paragraphs: [
      "We may disclose information to service providers that help operate TapToTable, including payment processors, cloud-hosting providers, database providers, email services, analytics providers, authentication and security services, and customer-support providers.",
      "Depending on the function being provided, service providers may process restaurant contact details, account records, technical information, subscription information, communications, or other data necessary to perform their services.",
      "We use third-party services for specific operational functions, such as Stripe for payment processing and Cloudflare Turnstile for signup security.",
      "Information may also be disclosed when required by law, legal process, court order, government request, or when reasonably necessary to protect rights, safety, security, or property.",
      "Information may be transferred as part of a merger, acquisition, financing, sale of assets, restructuring, or similar business transaction.",
    ],
  },
  {
    title: "11. Sale of Personal Information and Advertising",
    paragraphs: [
      "TapToTable does not sell submitted restaurant-owner contact information, account information, or customer activity information in exchange for money.",
      "TapToTable may collect advertising attribution information from marketing links to understand which campaigns lead to restaurant inquiries and subscriptions.",
      "Some privacy laws define certain disclosures for targeted advertising or cross-context behavioral advertising as a sale or sharing of personal information even when no money is exchanged. Whether such requirements apply depends on the technologies used and applicable law.",
      "If you have questions about how advertising or tracking information is processed, or wish to exercise an applicable privacy right, contact TapToTable using the email address below.",
    ],
  },
  {
    title: "12. Restaurant Pages and Public Information",
    paragraphs: [
      "Information displayed on an activated customer-facing TapToTable Page is public and may be viewed by anyone with access to the page link.",
      "Public content may include the restaurant name, address, phone number, hours, photographs, menu links, ordering links, website links, Promotions, Events, Featured Items, ratings, review counts, and customer action links.",
      "Featured Item pages may publicly display menu item names, descriptions, photographs, and customer action buttons.",
      "Restaurant owners should not submit private, confidential, or sensitive personal information for publication on a customer-facing TapToTable Page.",
    ],
  },
  {
    title: "13. Third-Party Links and Services",
    paragraphs: [
      "TapToTable Pages may link to third-party services, including restaurant websites, online-ordering providers, social-media platforms, mapping services, menu platforms, reservation providers, event platforms, and payment services.",
      "When you follow a third-party link, the destination service may collect information according to its own privacy policy and terms.",
      "TapToTable does not control the privacy, security, content, or data practices of third-party websites or services.",
    ],
  },
  {
    title: "14. Data Retention",
    paragraphs: [
      "We retain information for as long as reasonably necessary to provide TapToTable, maintain business and financial records, support customers, resolve disputes, enforce agreements, prevent fraud, and comply with legal obligations.",
      "Restaurant page requests, multi-location inquiries, restaurant information, communications, marketing attribution, analytics, subscription records, and account records may be retained after a review link expires, an account closes, or a subscription is canceled.",
      "Retention periods may vary depending on the type of information, the purpose for which it was collected, contractual obligations, technical limitations, and applicable law.",
    ],
  },
  {
    title: "15. Data Security and Signup Protection",
    paragraphs: [
      "We use reasonable administrative, technical, and organizational measures intended to protect information against unauthorized access, alteration, loss, misuse, or disclosure.",
      "TapToTable may use security measures such as Cloudflare Turnstile, rate limits, hidden form fields, and automated-submission detection to reduce spam and abuse.",
      "Security checks may process technical information such as browser characteristics, request information, and IP addresses to help distinguish legitimate visitors from automated activity.",
      "No website, database, transmission method, or storage system can be guaranteed completely secure. Account users are responsible for protecting their passwords and access to associated email accounts.",
      "Please contact us promptly if you believe your TapToTable account or information has been compromised.",
    ],
  },
  {
    title: "16. Access, Correction, and Deletion Requests",
    paragraphs: [
      `You may contact us at ${SUPPORT_EMAIL} to request access to, correction of, or deletion of personal information associated with you or your restaurant.`,
      "We may need to verify your identity or authority before completing a request. Information may be retained where necessary for billing, tax, fraud prevention, security, legal compliance, dispute resolution, or enforcement purposes.",
      "Restaurant owners can update supported restaurant information, Promotions, Events, Featured Items, photographs, links, and customer actions directly through their secure, easy-to-use TapToTable Dashboard.",
      "Privacy rights and response requirements may vary depending on applicable law and your location.",
    ],
  },
  {
    title: "17. Email Communications",
    paragraphs: [
      "We may send service-related emails concerning page requests, page creation, review links, account access, billing, subscriptions, security, support, updates, and other TapToTable matters.",
      "A restaurant page request may trigger a confirmation email to the requester and a notification to TapToTable staff or the business owner responsible for reviewing requests.",
      "Certain operational or transactional messages are necessary to provide the service and may continue while you maintain an account or active subscription.",
      "Marketing messages, when used, may include an unsubscribe option where required by law.",
    ],
  },
  {
    title: "18. Children's Privacy",
    paragraphs: [
      "TapToTable is a business service intended for restaurant owners, operators, employees, contractors, and adult customers. It is not directed to children under 13.",
      "We do not knowingly collect personal information directly from children under 13. If you believe a child has submitted personal information to TapToTable, contact us so we can review and address the matter.",
    ],
  },
  {
    title: "19. United States Operations",
    paragraphs: [
      "TapToTable is operated by Melted Port Studios LLC in the United States. Information may be stored and processed in the United States and in other locations where service providers operate.",
      "Information processed through TapToTable may be subject to privacy laws that differ depending on the location of the user, restaurant, or service provider.",
    ],
  },
  {
    title: "20. Changes to This Privacy Policy",
    paragraphs: [
      "We may update this Privacy Policy as TapToTable, applicable laws, or our service providers change.",
      "When the policy is updated, we will revise the date shown at the top of this page. Material changes may also be communicated through the website, dashboard, or email when appropriate or required.",
      "The updated policy applies from its stated effective date, subject to applicable law.",
    ],
  },
  {
    title: "21. Contact Us",
    paragraphs: [
      `For privacy questions, account requests, corrections, deletion requests, or questions about advertising attribution, contact TapToTable at ${SUPPORT_EMAIL}.`,
      "TapToTable is a service of Melted Port Studios LLC.",
    ],
  },
];

export default function Privacy() {
  return (
    <div
      className="min-h-screen bg-[#09090B] text-white"
      data-testid="privacy-page"
    >
      <Navbar />

      <main className="px-6 pb-24 pt-32 md:px-12">
        <motion.article
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="mx-auto max-w-4xl"
        >
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-1 text-xs text-zinc-500 transition-colors hover:text-white"
            data-testid="privacy-back-link"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back to home
          </Link>

          <div className="label-caps">Legal</div>

          <h1
            className="mt-3 font-display text-3xl font-semibold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl"
            data-testid="privacy-heading"
          >
            Privacy Policy
          </h1>

          <p className="mt-4 text-sm text-zinc-500">
            Last updated: {LAST_UPDATED}
          </p>

          <div className="mt-8 rounded-2xl border border-[#00E5FF]/25 bg-[#00E5FF]/10 p-5">
            <h2 className="font-display text-lg font-semibold text-white">
              Privacy overview
            </h2>

            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-zinc-300">
              {[
                "We collect information needed to build TapToTable Pages and provide the service.",
                "We may use public restaurant information to prepare and improve your page.",
                "Stripe processes subscription payments; TapToTable does not directly store complete card numbers.",
                "We collect supported page activity and may record marketing attribution information.",
                "We use security measures to help protect signup forms and accounts.",
                "We do not sell submitted restaurant contact information for money.",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3"
                >
                  <span className="mt-0.5 text-[#00E5FF]">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 space-y-10 text-base leading-relaxed text-zinc-300">
            {sections.map((section) => (
              <section key={section.title}>
                <h2 className="font-display text-xl font-semibold text-white md:text-2xl">
                  {section.title}
                </h2>

                {section.paragraphs && (
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                )}

                {section.bullets && (
                  <ul className="mt-4 space-y-3">
                    {section.bullets.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="mt-0.5 text-[#00E5FF]">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.title === "16. Access, Correction, and Deletion Requests" && (
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="mt-4 inline-flex text-sm font-semibold text-[#00E5FF] transition-colors hover:text-[#66FFFF]"
                    data-testid="privacy-request-email"
                  >
                    Submit a privacy request →
                  </a>
                )}

                {section.title === "21. Contact Us" && (
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="mt-4 inline-flex text-sm font-semibold text-[#00E5FF] transition-colors hover:text-[#66FFFF]"
                    data-testid="privacy-contact-email"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                )}
              </section>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-[#121214] p-5 text-sm leading-relaxed text-zinc-400">
            This Privacy Policy should be read together with the{" "}
            <Link
              to="/terms"
              className="font-semibold text-[#00E5FF] transition-colors hover:text-[#66FFFF]"
            >
              TapToTable Terms of Service
            </Link>
            .
          </div>
        </motion.article>
      </main>

      <Footer />
    </div>
  );
}
